"use client";

import { useCallback, useState } from "react";
import type { LoginPhoneState } from "@/lib/auth/login-phone.service";
import { routes } from "@/lib/routes";
import { formatPhoneNumber } from "@/lib/validators/phone";
import { useOtpChallenge, type OtpChallenge } from "@/components/auth/useOtpChallenge";
import { loginPhoneCopy } from "@/components/profile/login-phone-copy";
type LoginPhoneStep = "idle" | "editing" | "step_up" | "new_code" | "done";
export interface LoginPhoneChange {
  step: LoginPhoneStep;
  newPhone: string;
  setNewPhone: (phone: string) => void;
  /** Where the step-up code went: the old number's ending, or the masked e-mail. */
  stepUp: {
    factor: "phone" | "email";
    target: string;
  } | null;
  /** The new number's last four digits, once its code is on the way. */
  newTarget: string;
  /** The applied change, for the confirmation message. */
  result: {
    change: "added" | "replaced" | "unchanged";
    ending: string;
  } | null;
  submitError: string | null;
  submitting: boolean;
  stepUpChallenge: OtpChallenge;
  newCodeChallenge: OtpChallenge;
  start: () => void;
  cancel: () => void;
  /** Back to the number, kept as typed — a new code needs a new step-up. */
  restart: () => void;
  submitNumber: () => Promise<void>;
}
function post(url: string, body: Record<string, unknown>): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });
}
function dataOf(response: Record<string, unknown>): Record<string, unknown> {
  const data = response.data;
  return data && typeof data === "object" ? data as Record<string, unknown> : {};
}

/**
 * The profile's login-phone change (ARCH-055): enter the number, pass the
 * step-up code (old phone, or e-mail to add a first phone), then the new
 * number's code. Each step's verdict lives on the server; this hook only
 * walks the steps and remembers the proof between the last two.
 */
export function useLoginPhoneChange(options: {
  state: LoginPhoneState;
  onChanged: (next: LoginPhoneState) => void;
}): LoginPhoneChange {
  const {
    state,
    onChanged
  } = options;
  const [step, setStep] = useState<LoginPhoneStep>("idle");
  const [newPhone, setNewPhone] = useState("");
  const [stepUp, setStepUp] = useState<LoginPhoneChange["stepUp"]>(null);
  const [proof, setProof] = useState<string | null>(null);
  const [newTarget, setNewTarget] = useState("");
  const [result, setResult] = useState<LoginPhoneChange["result"]>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const stepUpChallenge = useOtpChallenge({
    initialStage: "code_sent",
    autoSubmit: true,
    requestCode: () => post(routes.api.user.loginPhone.stepUp, {
      newPhone
    }),
    submitCode: code => post(routes.api.user.loginPhone.verifyStepUp, {
      newPhone,
      code
    }),
    onVerified: response => {
      const data = dataOf(response);
      setProof(typeof data.proof === "string" ? data.proof : null);
      setNewTarget(typeof data.target === "string" ? data.target : "");
      setStep("new_code");
    }
  });
  const newCodeChallenge = useOtpChallenge({
    initialStage: "code_sent",
    autoSubmit: true,
    submitCode: code_0 => post(routes.api.user.loginPhone.confirm, {
      newPhone,
      code: code_0,
      proof
    }),
    onVerified: response_0 => {
      const data_0 = dataOf(response_0);
      const ending = typeof data_0.loginPhoneEnding === "string" ? data_0.loginPhoneEnding : "";
      const change = data_0.change === "added" || data_0.change === "unchanged" ? data_0.change : "replaced";
      setResult({
        change,
        ending
      });
      setStep("done");
      onChanged({
        ...state,
        // E.164, as the server reports it — not the field's composed text.
        loginPhone: formatPhoneNumber(newPhone),
        loginPhoneEnding: ending,
        stepUpFactor: "phone"
      });
    }
  });
  const start = useCallback(() => {
    setNewPhone("");
    setSubmitError(null);
    setResult(null);
    setStep("editing");
  }, []);
  const cancel = useCallback(() => {
    setSubmitError(null);
    setProof(null);
    setStep("idle");
  }, []);
  const restart = useCallback(() => {
    setSubmitError(null);
    setProof(null);
    setStep("editing");
  }, []);
  const {
    reset: resetStepUp
  } = stepUpChallenge;
  const {
    reset: resetNewCode
  } = newCodeChallenge;
  const submitNumber = useCallback(async () => {
    if (!newPhone || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await post(routes.api.user.loginPhone.stepUp, {
        newPhone
      });
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setSubmitError(res.status === 503 ? loginPhoneCopy.unavailable : typeof body.error === "string" ? body.error : "Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
        return;
      }
      const data_1 = dataOf(body);
      resetStepUp();
      resetNewCode();
      setStepUp({
        factor: data_1.factor === "email" ? "email" : "phone",
        target: typeof data_1.target === "string" ? data_1.target : ""
      });
      setStep("step_up");
    } catch {
      setSubmitError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  }, [newPhone, submitting, resetStepUp, resetNewCode]);
  return {
    step,
    newPhone,
    setNewPhone,
    stepUp,
    newTarget,
    result,
    submitError,
    submitting,
    stepUpChallenge,
    newCodeChallenge,
    start,
    cancel,
    restart,
    submitNumber
  };
}